import React, { useState, useMemo } from 'react';
import { DollarSign, Calculator, TrendingDown, FileText, Search, Shield, CheckCircle, ArrowRight, Mail, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Input } from './ui/input';

const CostSavingsCalculator = () => {
  const [contractsPerMonth, setContractsPerMonth] = useState(3);
  const [documentsConverted, setDocumentsConverted] = useState(10);
  const [contractReviews, setContractReviews] = useState(2);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Lawyer rates (average)
  const LAWYER_HOURLY_RATE = 350;
  const HOURS_PER_CONTRACT_REVIEW = 1.5;
  const HOURS_PER_DOCUMENT_TASK = 0.5;
  const CONTRACT_DRAFT_COST = 500;

  // Our pricing
  const OUR_MONTHLY_COST = 19;

  const calculations = useMemo(() => {
    // Lawyer costs
    const lawyerContractDrafting = contractsPerMonth * CONTRACT_DRAFT_COST;
    const lawyerDocumentWork = documentsConverted * HOURS_PER_DOCUMENT_TASK * LAWYER_HOURLY_RATE;
    const lawyerContractReview = contractReviews * HOURS_PER_CONTRACT_REVIEW * LAWYER_HOURLY_RATE;
    const totalLawyerCost = lawyerContractDrafting + lawyerDocumentWork + lawyerContractReview;

    // Our cost (just monthly subscription)
    const totalOurCost = OUR_MONTHLY_COST;

    // Savings
    const monthlySavings = totalLawyerCost - totalOurCost;
    const yearlySavings = monthlySavings * 12;
    const savingsPercentage = totalLawyerCost > 0 ? Math.round((monthlySavings / totalLawyerCost) * 100) : 0;

    return {
      lawyerContractDrafting,
      lawyerDocumentWork,
      lawyerContractReview,
      totalLawyerCost,
      totalOurCost,
      monthlySavings,
      yearlySavings,
      savingsPercentage
    };
  }, [contractsPerMonth, documentsConverted, contractReviews]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    
    setIsSubmitting(true);
    
    try {
      // Send to backend API
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/leads/calculator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          annual_savings: calculations.yearlySavings,
          monthly_savings: calculations.monthlySavings,
          contracts_per_month: contractsPerMonth,
          documents_converted: documentsConverted,
          contract_reviews: contractReviews,
          source: 'calculator'
        })
      });
      
      if (response.ok) {
        setIsSubscribed(true);
      } else {
        // Fallback to localStorage if API fails
        const leads = JSON.parse(localStorage.getItem('calculator_leads') || '[]');
        leads.push({
          email,
          savings: calculations.yearlySavings,
          contractsPerMonth,
          documentsConverted,
          contractReviews,
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('calculator_leads', JSON.stringify(leads));
        setIsSubscribed(true);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      // Fallback to localStorage
      const leads = JSON.parse(localStorage.getItem('calculator_leads') || '[]');
      leads.push({
        email,
        savings: calculations.yearlySavings,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('calculator_leads', JSON.stringify(leads));
      setIsSubscribed(true);
    }
    
    setIsSubmitting(false);
  };

  return (
    <section className="py-12 md:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full mb-4">
            <Calculator className="h-4 w-4 text-green-400 mr-2" />
            <span className="text-sm font-medium text-green-300">Cost Savings Calculator</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            See How Much You'll Save
          </h2>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Adjust the sliders to match your typical monthly document needs and see 
            the difference between lawyer fees and our affordable tools.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-start">
          {/* Calculator Inputs */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 md:p-8 border border-white/10">
            <h3 className="text-lg md:text-xl font-semibold text-white mb-6 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-400" />
              Your Monthly Document Needs
            </h3>

            {/* Contracts Created */}
            <div className="mb-6 md:mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm md:text-base text-slate-200 font-medium">Contracts Created</label>
                <span className="text-xl md:text-2xl font-bold text-white">{contractsPerMonth}</span>
              </div>
              <Slider
                value={[contractsPerMonth]}
                onValueChange={(value) => setContractsPerMonth(value[0])}
                max={20}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>0</span>
                <span>20/month</span>
              </div>
            </div>

            {/* Documents Converted */}
            <div className="mb-6 md:mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm md:text-base text-slate-200 font-medium">Documents Processed</label>
                <span className="text-xl md:text-2xl font-bold text-white">{documentsConverted}</span>
              </div>
              <Slider
                value={[documentsConverted]}
                onValueChange={(value) => setDocumentsConverted(value[0])}
                max={50}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>0</span>
                <span>50/month</span>
              </div>
            </div>

            {/* Contracts Reviewed */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm md:text-base text-slate-200 font-medium">Contracts Reviewed</label>
                <span className="text-xl md:text-2xl font-bold text-white">{contractReviews}</span>
              </div>
              <Slider
                value={[contractReviews]}
                onValueChange={(value) => setContractReviews(value[0])}
                max={10}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>0</span>
                <span>10/month</span>
              </div>
            </div>

            {/* Lawyer rate note */}
            <div className="bg-white/5 rounded-lg p-3 md:p-4 border border-white/10">
              <p className="text-xs md:text-sm text-slate-400">
                <strong className="text-slate-300">Based on:</strong> Average lawyer rate ${LAWYER_HOURLY_RATE}/hr, 
                ~${CONTRACT_DRAFT_COST}/contract draft
              </p>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4 md:space-y-6">
            {/* Comparison Cards */}
            <div className="grid grid-cols-1 gap-4">
              {/* Lawyer Cost */}
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-red-300 font-medium text-sm md:text-base">With a Lawyer</span>
                  <span className="text-xs text-red-400 bg-red-500/20 px-2 py-1 rounded">Typical cost</span>
                </div>
                <div className="text-3xl md:text-4xl font-bold text-red-400 mb-2 md:mb-3">
                  {formatCurrency(calculations.totalLawyerCost)}
                  <span className="text-base md:text-lg font-normal text-red-300">/month</span>
                </div>
                <div className="space-y-1 text-xs md:text-sm text-red-300/80">
                  <div className="flex justify-between">
                    <span>Contract drafting ({contractsPerMonth}x)</span>
                    <span>{formatCurrency(calculations.lawyerContractDrafting)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Document work ({documentsConverted}x)</span>
                    <span>{formatCurrency(calculations.lawyerDocumentWork)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Contract reviews ({contractReviews}x)</span>
                    <span>{formatCurrency(calculations.lawyerContractReview)}</span>
                  </div>
                </div>
              </div>

              {/* Our Cost */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-green-300 font-medium text-sm md:text-base">With Our Legal Suite</span>
                  <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">Your cost</span>
                </div>
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2 md:mb-3">
                  {formatCurrency(calculations.totalOurCost)}
                  <span className="text-base md:text-lg font-normal text-green-300">/month</span>
                </div>
                <div className="space-y-1 text-xs md:text-sm text-green-300/80">
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    <span>Unlimited document conversion</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    <span>Unlimited AI analysis</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    <span>Access to full Legal Suite</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Savings Summary */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-4 md:p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingDown className="h-5 w-5 md:h-6 md:w-6 text-white mr-2" />
                <span className="text-white/90 font-medium text-sm md:text-base">Your Savings</span>
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-1">
                {formatCurrency(calculations.monthlySavings)}
              </div>
              <div className="text-green-100 text-sm md:text-base mb-3 md:mb-4">per month ({calculations.savingsPercentage}% savings)</div>
              
              <div className="bg-white/20 rounded-lg p-3 md:p-4 mb-4">
                <div className="text-white/80 text-xs md:text-sm mb-1">Annual Savings</div>
                <div className="text-2xl md:text-3xl font-bold text-white">{formatCurrency(calculations.yearlySavings)}</div>
              </div>

              {/* Email Capture */}
              {!isSubscribed ? (
                <form onSubmit={handleEmailSubmit} className="space-y-3">
                  <p className="text-white/80 text-xs md:text-sm">Get your personalized savings report + tips to reduce legal costs:</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-white/90 border-0 text-slate-900 placeholder:text-slate-500"
                        required
                      />
                    </div>
                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-white text-green-700 hover:bg-green-50 font-semibold whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Get Report'
                      )}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="text-white text-center py-2">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-white" />
                  <p className="font-semibold">Thanks! Check your email for your savings report.</p>
                </div>
              )}
            </div>

            {/* Start Saving CTA */}
            <Button 
              size="lg"
              className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold text-base md:text-lg py-4 md:py-6"
              onClick={() => {
                document.getElementById('processor')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Start Saving Now — It's Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            {/* Trust note */}
            <div className="text-center text-slate-400 text-xs md:text-sm">
              <Shield className="h-4 w-4 inline mr-1" />
              Join 50,000+ small business owners saving on legal costs
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostSavingsCalculator;
